import axios from '@/lib/axios'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { Interface } from 'readline'
import { ref } from 'vue'
import { ToastInterface, useToast } from 'vue-toastification'

import { RateComment } from '@/interfaces/rateComment'

interface Props {
	immediate?: boolean
	params: Params
}

interface Params {
	id: string
}

interface CreateParams extends Params {
	commentary: string
}

const getRateComments = async (params: Params) => {
	const { data } = await axios.get<RateComment[]>(
		`/api/internal/rates/${params.id}/comments`
	)

	return data
}

const createRateComments = async (
	params: CreateParams,
	toast: ToastInterface
) => {
	try {
		const { data } = await axios.post<RateComment>(
			`/api/internal/rates/${params.id}/comments`,
			{
				commentary: params.commentary,
			}
		)

		return data
	} catch (error: any) {
		toast.error(error.response.data.message)
	}
}

const useRateComments = (props: Props) => {
	const rateId = ref<string>(props.params.id)
	const toast = useToast()

	const { data, isLoading, isFetching, refetch, isRefetching } = useQuery(
		['rateComments', rateId.value],
		() =>
			getRateComments({
				id: rateId.value,
			}),
		{
			enabled: props.immediate,
		}
	)

	const createCommentMutation = useMutation((params: CreateParams) =>
		createRateComments(params, toast)
	)

	return {
		rateId,
		data,
		isLoading,
		isFetching,
		isRefetching,
		refetch,
		createCommentMutation: createCommentMutation,
		createCommentMutationAsync: createCommentMutation.mutateAsync,
		createCommentMutationSuccess: createCommentMutation.isSuccess,
		createCommentMutationLoading: createCommentMutation.isLoading,
		createCommentMutationError: createCommentMutation.error,
	}
}

export default useRateComments
